/**
 * User: hella
 * Date: 5/17/14
 * Time: 5:59 PM
 * Copyright 1stdibs.com, Inc. 2014. All Rights Reserved.
 */

'use strict';

const $ = require('dibs-jquery-shim');
const SV = require('server-vars');
const tracking = require('dibs-tracking');
const strings = require('dibs-strings-helper');
const User = require('../../global/models/User');
const tokenReplace = require('dibs-token-replace');
const get = require('lodash.get');

const IMAGE_EVENT_CATEGORY = 'product';

const trackedProductDetailDimensions = [
    'timePeriod',
    'creator',
    'onSale',
    'newListing',
    'dealerName',
    'dealerID',
    'dealerLocation',
    'itemPostingLocation',
    'purchaseStatus',
    'askingPrice',
    'priceType',
];

// These lookup tables are used in trackGaImageEvent to build the action string for
// for events on the pdp Hero images and Zoom modal images
const imageActionStep = {
    next: 'pdp image carousel {key} button',
    previous: 'pdp image carousel {key} button',
    mobile: 'pdp secondary image mobile selector click',
    zoom: 'image zoomed',
};

/**
 * @param {*} label The current label being passed to GA
 * @returns {*}
 */
const getGaLabel = function(label) {
    // need this last null in there for FINDING-1485
    if (typeof label !== 'undefined' && label !== null) {
        return label.toString();
    } else if (label !== null) {
        return SV.data.gaString;
    }
    return null;
};

/**
 * @param {*} action The GA action
 * @returns {*}
 */
const getGaAction = function(action) {
    if (typeof action !== 'undefined' && action !== null) {
        return action;
    }
    return location.href;
};

/**
 * @param {String} value the value to update
 * @param {Boolean} check set to y or n
 */
const updateCustomVar = function(value, check) {
    const customVars = get(SV, 'tracking.customVars', false);

    if (!customVars) {
        return;
    }

    const len = customVars.length;

    for (let i = 0; i < len; i++) {
        const customVar = customVars[i];
        if (customVar.slot === 4) {
            const oldValue = customVar.value;
            customVar.value = exports.amendCustomVar(customVar.value, value, check);

            if (oldValue !== customVar.value) {
                //save in namespace to be consumed by gas.dibs.pageview.js->createCustomVar
                customVars[i] = customVar;
            }
        }
    }
};

exports.getInitialDataLayerInfo = function getInitialDataLayerInfo() {
    return get(SV, 'tracking.initialDataLayerInfo', false);
};

/**
 *
 * @param {String} customVar the customVar to update
 * @param {String} value the value to update
 * @param {Boolean} check set to y or n
 * @returns {String}
 */
exports.amendCustomVar = function amendCustomVar(customVar, value, check) {
    return strings.setInDelimitedString(customVar, value, check ? 'y' : 'n', '//|');
};

/**
 *
 * @param {string} category
 * @param {string} [action]
 * @param {string} [label]
 * @param {number} [value]
 * @param {boolean} [noninteraction]
 */
exports.trackGaEvent = function trackGaEvent(category, action, label, value, noninteraction) {
    action = getGaAction(action);
    label = getGaLabel(label);

    tracking.trackEvent({
        category: category,
        action: action,
        label: label,
        value: value,
        noninteraction: noninteraction,
    });
};

exports.updateGaLoginStatus = function updateGaLoginStatus() {
    updateCustomVar('log', User.loggedIn());
};

exports.updateGaReturns = function updateGaReturns(hasReturnPolicy) {
    updateCustomVar('ret', hasReturnPolicy);
};

exports.getGaPageSubtype = function getGaPageSubtype() {
    return exports.getInitialDataLayerInfo().pageSubType;
};

exports.updateGaPageSubtype = function updateGaPageSubtype(priceStates) {
    const initialDataLayerInfo = exports.getInitialDataLayerInfo();
    let status = 'Available-Price';
    let purchaseStatus;

    if (!initialDataLayerInfo) {
        return;
    }

    // Suspended PDPs should never hit here
    if (priceStates.showOnHold) {
        status = 'Hold';
        purchaseStatus = exports.amendCustomVar(initialDataLayerInfo.purchaseStatus, 'pur', false);
        initialDataLayerInfo.purchaseStatus = purchaseStatus;
    } else if (priceStates.showSold) {
        status = 'Sold';
    } else if (priceStates.showNetPrice) {
        status = 'Available-Net Price';
    } else if (priceStates.showPriceUponRequest) {
        status = 'Available-No Price';
    }

    if (priceStates.showOnHoldCustom) {
        status += '-hold';
    }

    initialDataLayerInfo.pageSubType = `PDP-${status}`;
};

exports.unblockCustomVar = function unblockCustomVar() {
    $.publish('ga:unblockCustomVar');
};

/**
 * Tracks interaction with the carousel images on pdp pages
 * @param actionStepKey
 * @param locationKey
 * @param label
 */
exports.trackGaImgEvent = function trackGaImgEvent(actionStepKey, label) {
    let action = imageActionStep[actionStepKey];

    if (action) {
        action = tokenReplace(action, { key: actionStepKey });
        this.trackGaEvent(IMAGE_EVENT_CATEGORY, action, label);
    }
};

exports.trackSuggestionLinkEvent = function trackSuggestionLinkEvent(e) {
    const data = $(e.target).data() || {};
    this.trackGaEvent(
        'PDP - Item Not For Sale - Related Item Clicked',
        data.gaaction,
        data.galabel
    );
};

exports.setupShippingStatusTracking = function() {
    tracking.setupShippingStatusTracking();
};

/**
 * Builds all page data that is derived from the user
 * @param gtmData
 * @param gtmUserAttributes
 */
exports.trackShippingStatusData = function(quotes = {}, canRequestShippingQuote) {
    const shipPreQuote = get(quotes, 'shipmentQuotes', []).length ? 'y' : 'n';
    const quotesDomestic = get(quotes, 'domestic', []);
    const quotesLocal = get(quotes, 'local', []);
    const shipPreQuoteToLoc = quotesDomestic.length || quotesLocal.length ? 'y' : 'n';

    tracking.resolveShippingStatusDfdWith({
        shippingStatus: `shipPreQuote=${shipPreQuote}|shipPreQuoteToLoc=${
            shipPreQuoteToLoc
        }|requestQuote=${canRequestShippingQuote}`,
    });
};

exports.trackProductDetail = function trackProductDetail(input = {}) {
    const data = SV.get('data');
    const dimensionData = Object.assign({}, input, SV.get('tracking.initialDataLayerInfo'));
    tracking.trackEcommerce({
        type: 'productDetail',
        products: [
            {
                url: data.canonicalUrl,
                id: data.pk,
                name: data.titleCondensed,
                brand: data.dealerPk,
                price: dimensionData.askingPrice,
                additional: tracking.getCustomDimensions(
                    dimensionData,
                    trackedProductDetailDimensions
                ),
            },
        ],
    });
};

exports.checkListingStatus = function checkListingStatus(listingStatuses, trackingOverrides) {
    listingStatuses = listingStatuses || {};
    trackingOverrides = trackingOverrides || {};
    const publishedKeys = Object.keys(listingStatuses)
        // filter out keys that aren't currently PUBLISHED
        .filter(key => get(listingStatuses, `${key}.0`) === 'PUBLISHED')
        // flatten to an object so there's no need for an array check
        .reduce((acc, val) => Object.assign({}, acc, { [val]: true }), {});

    if (publishedKeys.FEATURED) {
        trackingOverrides.itemPostingLocation = 'New Listings';
    } else if (publishedKeys.MARKETPLACE) {
        trackingOverrides.itemPostingLocation = 'Marketplace';
    } else if (publishedKeys.DEALER_STOREFRONT) {
        trackingOverrides.itemPostingLocation = 'Storefront';
    }

    return trackingOverrides;
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/helpers/pdpTracking.es.js